import React, { useState, useRef, useEffect } from "react";
import "./ExpandableCell.css";

export interface ExpandableCellProps {
  /** Text to show. Falsy values render as "-". */
  text?: string | null;
  /** Number of lines to show when collapsed. Default 2. */
  /** Optional class name for the outer <td>. */
  className?: string;
}

/**
 * Reusable table cell that shows long text with inline expand/collapse:
 * - "Show more" / "Show less" toggle (only when content overflows or when expanded).
 * - Smooth CSS transition; only the cell content expands.
 */
const ExpandableCell: React.FC<ExpandableCellProps> = ({
  text,
  className = "",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLSpanElement>(null);

  const displayText = text?.trim() ? text : "-";
  const isPlaceholder = displayText === "-";
  /** Only offer expand/collapse when text is comma-separated (e.g. multiple reasons); skip for short sentences. */
  const isCommaSeparated = !isPlaceholder && displayText.includes(",");
  const showToggle = isCommaSeparated && (hasOverflow || expanded);

  useEffect(() => {
    if (!isCommaSeparated || expanded) {
      setHasOverflow(false);
      return;
    }
    const el = contentRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollHeight > el.clientHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayText, isCommaSeparated, expanded]);

  const toggle = () => setExpanded((prev) => !prev);

  /* No comma in text: show full text only, no toggle */
  if (!isCommaSeparated) {
    return (
      <td className={`expandable-cell expandable-cell--plain ${className}`.trim()}>
        <span className="expandable-cell__content">{displayText}</span>
      </td>
    );
  }

  return (
    <td className={`expandable-cell ${className}`.trim()}>
      <div className="expandable-cell__inner">
        <div
          className={`expandable-cell__wrapper ${
            expanded ? "expandable-cell__wrapper--expanded" : "expandable-cell__wrapper--collapsed"
          }`}
        >
          <span ref={contentRef} className="expandable-cell__content">
            {displayText}
          </span>
        </div>
        {showToggle && (
          <button
            type="button"
            className="expandable-cell__toggle"
            onClick={toggle}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </td>
  );
};

export default ExpandableCell;
